import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import AuthMenu from './auth/AuthMenu';
import AuthModal from './auth/AuthModal';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../context/AuthContext';

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Logged-in users (customers or owners) don't need the salon-owner sales
  // pitch — only show marketing links + the lead CTA to logged-out visitors.
  const isLoggedIn = !!session && session.role !== 'unknown';

  // A salon's shared booking page (/shop/:slug) should read as the salon's own
  // page, not the Calendyz sales site: hide the B2B marketing nav, the lead CTA,
  // and the header login (customers sign in via the in-page "Log in to book").
  // Logged-in users still keep their account menu.
  const isShopPage = location.pathname.startsWith('/shop/');
  const showMarketing = !isLoggedIn && !isShopPage;
  const showAuthMenu = isLoggedIn || !isShopPage;

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
      window.scrollTo(0, 0);
    }
  };

  const handleNavClick = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  // Open the shared lead form rendered on the landing page. If we're not on the
  // home page, navigate there first, then dispatch once it has mounted.
  const handleStartFree = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (location.pathname === '/') {
      window.dispatchEvent(new Event('calendyz:open-lead'));
    } else {
      navigate('/');
      setTimeout(() => window.dispatchEvent(new Event('calendyz:open-lead')), 250);
    }
  };

  const navLinks = [
    { id: 'features', label: 'Features' },
    { id: 'for-salons', label: 'For salons' },
    { id: 'pricing', label: 'Pricing' },
  ];

  return (
    <div className="bg-surface text-fg flex min-h-screen flex-col font-sans">
      <header className="border-subtle bg-surface/80 sticky top-0 z-40 border-b backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link
              to="/"
              onClick={handleLogoClick}
              className="font-display text-2xl font-semibold tracking-tight"
            >
              <span>Calendyz.</span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden items-center space-x-6 md:flex">
              {showMarketing &&
                navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={(e) => handleNavClick(e, link.id)}
                    className="hover:text-accent text-fg-secondary cursor-pointer text-sm font-medium transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
              {showAuthMenu && <AuthMenu variant="desktop" />}
              <ThemeToggle variant="desktop" />
              {showMarketing && (
                <button
                  onClick={handleStartFree}
                  className="bg-brand-600 hover:bg-brand-700 rounded-xl px-4 py-2 text-sm font-medium text-white transition-colors"
                >
                  Start free
                </button>
              )}
            </nav>

            {/* Mobile controls. The theme toggle sits outside the hamburger
                condition so it stays reachable on shop pages, which render
                neither the marketing nav nor the auth menu. */}
            <div className="flex items-center gap-1 md:hidden">
              <ThemeToggle variant="desktop" />
              {(showMarketing || showAuthMenu) && (
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="hover:text-accent text-fg-secondary cursor-pointer p-2 transition-colors"
                  aria-label="Toggle menu"
                >
                  {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <nav className="border-subtle bg-surface/95 border-t backdrop-blur-md md:hidden">
            <div className="mx-auto flex max-w-7xl flex-col space-y-4 px-4 py-4 sm:px-6">
              {showMarketing &&
                navLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={(e) => handleNavClick(e, link.id)}
                    className="hover:text-accent text-fg-secondary cursor-pointer text-left text-sm font-medium transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
              <div className="border-subtle flex flex-col space-y-4 border-t pt-4">
                {showAuthMenu && (
                  <AuthMenu variant="mobile" onNavigate={() => setMobileMenuOpen(false)} />
                )}
                {showMarketing && (
                  <button
                    onClick={handleStartFree}
                    className="bg-brand-600 hover:bg-brand-700 rounded-xl px-4 py-3 text-center text-sm font-medium text-white transition-colors"
                  >
                    Start free
                  </button>
                )}
                <ThemeToggle variant="mobile" />
              </div>
            </div>
          </nav>
        )}
      </header>

      <main className="flex-grow">
        <Outlet />
      </main>

      <footer className="border-subtle bg-canvas mt-auto border-t">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
            {/* Logo & tagline */}
            <div>
              <Link to="/" className="font-display text-xl font-semibold tracking-tight">
                Calendyz.
              </Link>
              <p className="text-fg-muted mt-2 text-sm">
                Online booking and appointment management for beauty professionals.
              </p>
            </div>

            {/* Quick links */}
            <div>
              <h4 className="text-fg-subtle text-xs font-semibold tracking-widest uppercase">
                Quick Links
              </h4>
              <ul className="mt-3 space-y-2">
                <li>
                  <button
                    onClick={(e) => handleNavClick(e, 'features')}
                    className="hover:text-accent text-fg-secondary cursor-pointer text-sm transition-colors"
                  >
                    Features
                  </button>
                </li>
                <li>
                  <button
                    onClick={(e) => handleNavClick(e, 'pricing')}
                    className="hover:text-accent text-fg-secondary cursor-pointer text-sm transition-colors"
                  >
                    Pricing
                  </button>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-accent text-fg-secondary text-sm transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-accent text-fg-secondary text-sm transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-fg-subtle text-xs font-semibold tracking-widest uppercase">
                Contact
              </h4>
              <ul className="text-fg-secondary mt-3 space-y-2 text-sm">
                <li>support@calendyz.com</li>
                <li>Mon – Sat, 9am – 6pm</li>
              </ul>
            </div>
          </div>

          <div className="border-default text-fg-subtle mt-10 flex flex-col items-center gap-3 border-t pt-6 text-sm sm:flex-row sm:justify-between">
            <span>&copy; {new Date().getFullYear()} Calendyz. All rights reserved.</span>
            <div className="flex gap-4">
              <Link to="/privacy" className="hover:text-accent transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-accent transition-colors">
                Terms
              </Link>
            </div>
          </div>
        </div>
      </footer>

      <AuthModal />
    </div>
  );
}
